import { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { KeenIcon, LazyFormikSelect, CustomOption } from '@/components';
import { useContactOptions } from '@/hooks';
import { signerColor, signerDisplay } from './esignSigners';

const signerSchema = Yup.object().shape({
  name: Yup.string().trim().required('Name is required'),
  email: Yup.string().trim().email('Enter a valid email address').required('Email is required'),
});

/**
 * Add or edit one signer: pick a firm contact (fills name and email), or
 * type them. People who signed for this contact before are offered first.
 */
const SignerForm = ({ initial, previousSigners, onSave, onCancel }) => {
  const loadContactOptions = useContactOptions({ defaultPageSize: 20, filterByEmail: true });

  return (
    <Formik
      initialValues={{ contact: null, name: initial?.name || '', email: initial?.email || '' }}
      validationSchema={signerSchema}
      onSubmit={(values) => onSave({ name: values.name.trim(), email: values.email.trim() })}
    >
      {({ setFieldValue }) => (
        <Form className="mt-2 p-2 border border-dashed rounded-md bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-start">
            <div>
              <label className="text-xs form-label mb-1">Pick a contact</label>
              <LazyFormikSelect
                name="contact"
                placeholder="Search contacts"
                loadOptions={loadContactOptions}
                pageSize={20}
                components={{ Option: CustomOption }}
                customClassNames={{ menuList: '!max-h-[160px]' }}
                onOptionSelect={(selected) => {
                  const contact = selected?.original;
                  if (!contact) return;
                  setFieldValue('name', [contact.first_name, contact.last_name].filter(Boolean).join(' '));
                  setFieldValue('email', contact.email || '');
                }}
              />
              {previousSigners.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {previousSigners.slice(0, 5).map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className="badge badge-xs badge-outline"
                      title={opt.signer_email}
                      onClick={() => {
                        setFieldValue('name', opt.signer_name || '');
                        setFieldValue('email', opt.signer_email || '');
                      }}
                    >
                      {opt.signer_name || opt.signer_email}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="text-xs form-label mb-1">Name <span className="text-red-600">*</span></label>
              <Field name="name" className="input input-sm" placeholder="Full name" />
              <ErrorMessage name="name" component="div" className="mt-1 text-xs text-red-500" />
            </div>
            <div>
              <label className="text-xs form-label mb-1">Email <span className="text-red-600">*</span></label>
              <Field name="email" type="email" className="input input-sm" placeholder="name@example.com" />
              <ErrorMessage name="email" component="div" className="mt-1 text-xs text-red-500" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button type="submit" className="btn btn-xs btn-primary">{initial ? 'Save signer' : 'Add signer'}</button>
            <button type="button" className="btn btn-xs btn-light" onClick={onCancel}>Cancel</button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

/**
 * OL-203: the people who sign this document, in signing order. Clicking one
 * makes them the signer of the next Signature or Initials field placed.
 */
const EsignSignersBar = ({
  signers,
  activeKey,
  fieldCounts,
  recipient,
  forMany,
  previousSigners = [],
  onSelect,
  onAdd,
  onUpdate,
  onRemove,
  onMove,
}) => {
  // null = closed, 'new' = adding, or the key of the signer being edited
  const [editing, setEditing] = useState(null);
  const editingSigner = signers.find((s) => s.key === editing);
  const stop = (fn) => (e) => {
    e.stopPropagation();
    fn();
  };

  return (
    <div className="border rounded-md p-2 my-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
        <div className="text-xs">
          <span className="font-semibold">Signers</span>
          <span className="text-gray-600"> sign in this order. Click a signer, then place their Signature or Initials fields. Other fields are filled in by signer 1.</span>
        </div>
        <button type="button" className="btn btn-xs btn-light" onClick={() => setEditing('new')}>
          <KeenIcon icon="plus" /> Add signer
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {signers.map((signer, index) => {
          const color = signerColor(index);
          const active = signer.key === activeKey;
          const { name, email } = signerDisplay(signer, { recipient, forMany });
          const count = fieldCounts[signer.key] || 0;
          // When sending to many, the recipient is the whole point and stays.
          const removable = signers.length > 1 && !(forMany && signer.role === 'recipient');

          return (
            <div
              key={signer.key}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(signer.key)}
              onKeyDown={(e) => { if (e.key === 'Enter') onSelect(signer.key); }}
              className="flex items-center gap-1.5 rounded-md border-2 px-2 py-1 text-xs cursor-pointer"
              style={{ borderColor: color, backgroundColor: active ? `${color}1a` : undefined }}
              title={active ? 'New Signature and Initials fields go to this signer' : 'Click to place fields for this signer'}
            >
              <span className="size-4 shrink-0 rounded-full text-white text-[10px] flex items-center justify-center" style={{ backgroundColor: color }}>
                {index + 1}
              </span>
              <span className="font-medium">{name}</span>
              {email && <span className="text-gray-500 hidden sm:inline">{email}</span>}
              <span className={count ? 'text-gray-500' : 'text-red-600'}>
                · {count} {count === 1 ? 'field' : 'fields'}
              </span>
              {index > 0 && (
                <button type="button" className="text-gray-500 hover:text-gray-900" title="Sign earlier" onClick={stop(() => onMove(signer.key, -1))}>
                  <KeenIcon icon="arrow-left" />
                </button>
              )}
              {index < signers.length - 1 && (
                <button type="button" className="text-gray-500 hover:text-gray-900" title="Sign later" onClick={stop(() => onMove(signer.key, 1))}>
                  <KeenIcon icon="arrow-right" />
                </button>
              )}
              {signer.role !== 'recipient' && (
                <button type="button" className="text-gray-500 hover:text-gray-900" title="Edit signer" onClick={stop(() => setEditing(signer.key))}>
                  <KeenIcon icon="pencil" />
                </button>
              )}
              {removable && (
                <button type="button" className="text-red-600" title="Remove signer" onClick={stop(() => onRemove(signer.key))}>
                  <KeenIcon icon="cross" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {editing && (
        <SignerForm
          key={editing}
          initial={editingSigner}
          previousSigners={previousSigners}
          onCancel={() => setEditing(null)}
          onSave={(values) => {
            if (editingSigner) onUpdate(editingSigner.key, values);
            else onAdd(values);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

export { EsignSignersBar };
